import { ONLINE_FIRST_ENTITIES } from '../constants/configs';
import { ENVIRONMENT_CONFIG } from '../constants/environmentConfig';
import { generateUniqueRefId } from './refIdGenerator';

export interface SyncItem {
  id: string;
  entity: string;
  action: 'create' | 'update' | 'soft-delete';
  data: Record<string, unknown>;
  timestamp: number;
  retryCount: number;
  synced: number;
}

export interface LocalData {
  id: string | number;
  entity: string;
  data: Record<string, unknown>;
  timestamp: number;
  synced: number;
}

export interface SyncMap {
  entity: string;
  lastSyncTime: number;
  lastSyncOperationTime?: number;
  syncStatus: 'synced' | 'pending' | 'error';
}

export interface ReferenceData {
  id: number;
  entity: string;
  data: Record<string, unknown>;
  updateTime: number;
  lastSynced: number;
}

export class IndexedDBManager {
  private db: IDBDatabase | null = null;
  private readonly dbName = 'OHCPLocalDB';
  private readonly version = ENVIRONMENT_CONFIG.INDEXED_DB_VERSION;
  private readonly sessionOwnerSettingKey = 'sessionOwner';
  private initPromise: Promise<void> | null = null;

  constructor() {
    this.initPromise = this.init();
  }

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = window.indexedDB.open(this.dbName, this.version);

      request.onerror = () => {
        if (request.error?.name === 'VersionError') {
          this.resetDatabase()
            .then(() => {
              this.init().then(resolve).catch(reject);
            })
            .catch(() => {
              this.forceResetDatabase()
                .then(() => this.init().then(resolve).catch(reject))
                .catch(reject);
            });
        } else {
          reject(request.error);
        }
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Rebuild the local IndexedDB schema whenever VITE_INDEXED_DB_VERSION is increased.
        // This clears cached/offline data, then recreates only the currently supported stores.
        Array.from(db.objectStoreNames).forEach((storeName) => {
          db.deleteObjectStore(storeName);
        });

        // Pending local mutations that need to be sent to the backend.
        const syncStore = db.createObjectStore('syncQueue', { keyPath: 'id' });
        syncStore.createIndex('entity', 'entity', { unique: false });
        syncStore.createIndex('synced', 'synced', { unique: false });
        syncStore.createIndex('timestamp', 'timestamp', { unique: false });

        // Presentation cache and locally created presentation records.
        const presentationStore = db.createObjectStore('presentations', { keyPath: 'id' });
        presentationStore.createIndex('category', 'category', { unique: false });
        presentationStore.createIndex('synced', 'synced', { unique: false });
        presentationStore.createIndex('barcode', 'data.barcode', { unique: false });

        // Price records collected or cached on the device.
        const priceStore = db.createObjectStore('price', { keyPath: 'id' });
        priceStore.createIndex('synced', 'synced', { unique: false });
        priceStore.createIndex('refId', 'refId', { unique: false });

        // Inspection records and their sync state.
        const inspectionStore = db.createObjectStore('inspection', { keyPath: 'id' });
        inspectionStore.createIndex('synced', 'synced', { unique: false });
        inspectionStore.createIndex('refId', 'refId', { unique: false });

        if (!db.objectStoreNames.contains('convertibleUnitGroups')) {
          db.createObjectStore('convertibleUnitGroups', { keyPath: 'id' });
        }

        if (!db.objectStoreNames.contains('establishments')) {
          const establishmentStore = db.createObjectStore('establishments', { keyPath: 'id' });
          establishmentStore.createIndex('updateTime', 'updateTime', { unique: false });
          establishmentStore.createIndex('lastSynced', 'lastSynced', { unique: false });
        }

        if (!db.objectStoreNames.contains('establishmentTypes')) {
          const establishmentTypeStore = db.createObjectStore('establishmentTypes', { keyPath: 'id' });
          establishmentTypeStore.createIndex('updateTime', 'updateTime', { unique: false });
          establishmentTypeStore.createIndex('lastSynced', 'lastSynced', { unique: false });
          establishmentTypeStore.createIndex('key', 'data.key', { unique: false });
        }

        // Reference data for locations.
        const locationStore = db.createObjectStore('locations', { keyPath: 'id' });
        locationStore.createIndex('updateTime', 'updateTime', { unique: false });
        locationStore.createIndex('lastSynced', 'lastSynced', { unique: false });
        locationStore.createIndex('district', 'data.district', { unique: false });

        // Reference data for product categories.
        const categoryStore = db.createObjectStore('productCategories', { keyPath: 'id' });
        categoryStore.createIndex('updateTime', 'updateTime', { unique: false });
        categoryStore.createIndex('lastSynced', 'lastSynced', { unique: false });
        categoryStore.createIndex('mainCategory', 'data.mainCategory', { unique: false });

        // Reference data for main products used by basket/product assignment flows.
        const mainProductStore = db.createObjectStore('mainProducts', { keyPath: 'id' });
        mainProductStore.createIndex('updateTime', 'updateTime', { unique: false });
        mainProductStore.createIndex('lastSynced', 'lastSynced', { unique: false });
        mainProductStore.createIndex('synced', 'synced', { unique: false });

        // Basket records and their sync state.
        const basketStore = db.createObjectStore('baskets', { keyPath: 'id' });
        basketStore.createIndex('updateTime', 'updateTime', { unique: false });
        basketStore.createIndex('lastSynced', 'lastSynced', { unique: false });
        basketStore.createIndex('synced', 'synced', { unique: false });

        // Task records available offline.
        const taskStore = db.createObjectStore('tasks', { keyPath: 'id' });
        taskStore.createIndex('updateTime', 'updateTime', { unique: false });
        taskStore.createIndex('synced', 'synced', { unique: false });

        // Reference data for task type options.
        const taskTypeStore = db.createObjectStore('taskTypes', { keyPath: 'id' });
        taskTypeStore.createIndex('updateTime', 'updateTime', { unique: false });
        taskTypeStore.createIndex('lastSynced', 'lastSynced', { unique: false });
        taskTypeStore.createIndex('key', 'data.key', { unique: false });

        // Per-entity sync metadata, such as last successful sync time.
        const syncMapStore = db.createObjectStore('syncMaps', { keyPath: 'entity' });
        syncMapStore.createIndex('lastSyncTime', 'lastSyncTime', { unique: false });
        syncMapStore.createIndex('syncStatus', 'syncStatus', { unique: false });

        // App-level local settings such as last sync timestamps.
        db.createObjectStore('settings', { keyPath: 'key' });
      };
    });
  }

  private async ensureInitialized(): Promise<void> {
    if (this.db) return;

    try {
      if (!this.initPromise) {
        this.initPromise = this.init();
      }
      await this.initPromise;

      // Small delay to ensure database is fully ready
      await new Promise((resolve) => setTimeout(resolve, 10));
    } catch (error) {
      this.initPromise = null;
      this.db = null;
      throw new Error('Database initialization failed');
    }
  }

  private createTransaction(
    storeNames: string | string[],
    mode: IDBTransactionMode = 'readonly'
  ): IDBTransaction {
    if (!this.db) {
      throw new Error('Database connection is not available');
    }

    try {
      return this.db.transaction(storeNames, mode);
    } catch (error) {
      throw new Error(`Failed to create transaction: ${error}`);
    }
  }

  private getStoreName(entity: string): string {
    const storeNameMap: Record<string, string> = {
      presentation: 'presentations',
      basket: 'baskets',
      price: 'price',
      inspection: 'inspection',
      establishments: 'establishments',
      establishmentTypes: 'establishmentTypes',
      locations: 'locations',
      productCategories: 'productCategories',
      task: 'tasks',
      task_type: 'taskTypes',
      mainProduct: 'mainProducts',
    };

    return storeNameMap[entity] || entity;
  }

  async addToSyncQueue(item: Omit<SyncItem, 'id'>): Promise<void> {
    await this.ensureInitialized();

    try {
      const syncItem: SyncItem = {
        ...item,
        id: `${item.entity}_${item.action}_${Date.now()}_${Math.random()}`,
        synced: 0,
      };

      const transaction = this.createTransaction(['syncQueue'], 'readwrite');
      const store = transaction.objectStore('syncQueue');
      await this.promisifyRequest(store.add(syncItem));
    } catch (error) {
      console.warn('Failed to add item to sync queue:', error);
    }
  }

  async getUnsyncedItems(): Promise<SyncItem[]> {
    await this.ensureInitialized();

    try {
      const transaction = this.createTransaction(['syncQueue'], 'readonly');
      const store = transaction.objectStore('syncQueue');
      const index = store.index('synced');
      const request = index.getAll(IDBKeyRange.only(0));

      return await this.promisifyRequest(request);
    } catch (error) {
      console.warn('Failed to get unsynced items:', error);
      return [];
    }
  }

  async markAsSynced(id: string): Promise<void> {
    await this.ensureInitialized();

    try {
      const transaction = this.createTransaction(['syncQueue'], 'readwrite');
      const store = transaction.objectStore('syncQueue');
      const item = await this.promisifyRequest(store.get(id));

      if (item) {
        item.synced = 1;
        await this.promisifyRequest(store.put(item));
      }
    } catch (error) {
      console.warn('Failed to mark item as synced:', error);
    }
  }

  async incrementRetryCount(id: string): Promise<void> {
    await this.ensureInitialized();

    const transaction = this.db!.transaction(['syncQueue'], 'readwrite');
    const store = transaction.objectStore('syncQueue');
    const item = await this.promisifyRequest(store.get(id));

    if (item) {
      item.retryCount += 1;
      await this.promisifyRequest(store.put(item));
    }
  }

  async storeLocalData(entity: string, data: any): Promise<void> {
    await this.ensureInitialized();

    const isOnlineFirst = ONLINE_FIRST_ENTITIES.has(entity);

    data = isOnlineFirst && data?.data ? data.data : data;

    let refId = isOnlineFirst && data?.id ? data.id : data?.refId;

    if (!refId) {
      refId = isOnlineFirst
        ? data?.id
          ? data.id
          : generateUniqueRefId(entity)
        : generateUniqueRefId(entity);
      data = isOnlineFirst ? data : { ...data, refId };
    }

    const localData: LocalData = {
      id: refId,
      entity,
      data,
      timestamp: Date.now(),
      synced: isOnlineFirst ? 1 : data.id ? 1 : 0,
    };

    const storeName = this.getStoreName(entity);

    const transaction = this.db!.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);

    try {
      if (isOnlineFirst && data?.refId && String(data.refId) !== String(refId)) {
        await this.promisifyRequest(store.delete(String(data.refId)));
      }

      await this.promisifyRequest(store.put(localData));
    } catch (error) {
      console.error(`Error writing to [${storeName}]:`, error);
    }
  }

  async getLocalData(entity: string, id?: string): Promise<any> {
    await this.ensureInitialized();

    try {
      const storeName = this.getStoreName(entity);
      const transaction = this.createTransaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);

      if (id) {
        const key =
          ONLINE_FIRST_ENTITIES.has(entity) && !Number.isNaN(Number(id))
            ? Number(id)
            : id.toString();
        return await this.promisifyRequest(store.get(key));
      } else {
        return await this.promisifyRequest(store.getAll());
      }
    } catch (error) {
      console.warn(`Failed to get local data for ${entity}:`, error);
      return [];
    }
  }

  async updateLocalData(entity: string, id: string, data: Record<string, unknown>): Promise<void> {
    await this.ensureInitialized();

    const storeName = this.getStoreName(entity);
    const transaction = this.db!.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    const key = ONLINE_FIRST_ENTITIES.has(entity) && !Number.isNaN(Number(id)) ? Number(id) : id;
    let existing = await this.promisifyRequest(store.get(key));

    if (!existing && typeof key === 'string' && !Number.isNaN(Number(key))) {
      existing = await this.promisifyRequest(store.get(Number(key)));
    }

    if (existing) {
      existing.data = { ...existing.data, ...data };
      existing.timestamp = Date.now();
      existing.synced = ONLINE_FIRST_ENTITIES.has(entity) ? 1 : 0;
      try {
        await this.promisifyRequest(store.put(existing));
      } catch (error) {
        console.error(`Failed updateLocalData [${storeName}]:`, error);
      }
    }
  }

  async deleteLocalData(entity: string, id: string): Promise<void> {
    await this.ensureInitialized();

    const storeName = this.getStoreName(entity);
    const transaction = this.db!.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    const key = ONLINE_FIRST_ENTITIES.has(entity) && !Number.isNaN(Number(id)) ? Number(id) : id;
    await this.promisifyRequest(store.delete(key));
  }

  async clearSyncQueue(): Promise<void> {
    await this.ensureInitialized();

    const transaction = this.db!.transaction(['syncQueue'], 'readwrite');
    const store = transaction.objectStore('syncQueue');
    await this.promisifyRequest(store.clear());
  }

  async storeReferenceData(entity: string, items: Record<string, unknown>[]): Promise<void> {
    await this.ensureInitialized();

    const storeName = this.getStoreName(entity);
    const transaction = this.db!.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);

    for (const item of items) {
      try {
        const itemId = Number(item.id);
        const existingItem = await this.promisifyRequest(store.get(itemId));

        const referenceData: ReferenceData = {
          id: itemId,
          entity,
          data: item,
          updateTime: Number(item.updateTime),
          lastSynced: Date.now(),
        };

        if (existingItem) {
          const existingUpdateTime = existingItem.updateTime || 0;
          const itemUpdateTime = Number(item.updateTime) || Date.now();

          if (itemUpdateTime > existingUpdateTime) {
            await this.promisifyRequest(store.put(referenceData));
          }
        } else {
          await this.promisifyRequest(store.add(referenceData));
        }
      } catch {
        // Error upserting reference item
      }
    }
  }

  async getReferenceData(entity: string, id?: number | string): Promise<ReferenceData[]> {
    await this.ensureInitialized();

    const storeName = this.getStoreName(entity);
    const transaction = this.db!.transaction([storeName], 'readonly');
    const store = transaction.objectStore(storeName);

    if (id) {
      const result = await this.promisifyRequest(store.get(id));
      return result ? [result] : [];
    } else {
      return await this.promisifyRequest(store.getAll());
    }
  }

  async getByBarcode(entity: string, barcode: string): Promise<any> {
    await this.ensureInitialized();
    if (entity !== 'presentation') {
      return null;
    }

    const storeName = 'presentations';
    const transaction = this.db!.transaction([storeName], 'readonly');
    const store = transaction.objectStore(storeName);

    // Check if index exists before using it
    if (!store.indexNames.contains('barcode')) {
      console.warn('Barcode index not found on presentations store');
      return null;
    }

    const index = store.index('barcode');
    // Since the index keyPath is 'data.barcode', searching for the barcode string
    // will strictly match the value inside data.barcode.
    const result = await this.promisifyRequest(index.get(barcode));

    return result || null;
  }

  async updateSyncMap(entity: string, syncMap: Partial<SyncMap>): Promise<void> {
    await this.ensureInitialized();

    const transaction = this.db!.transaction(['syncMaps'], 'readwrite');
    const store = transaction.objectStore('syncMaps');

    const existing = await this.promisifyRequest(store.get(entity));
    const updatedSyncMap: SyncMap = {
      entity,
      lastSyncTime: syncMap.lastSyncTime ?? existing?.lastSyncTime ?? 0,
      lastSyncOperationTime: syncMap.lastSyncOperationTime ?? existing?.lastSyncOperationTime ?? 0,
      syncStatus: syncMap.syncStatus ?? existing?.syncStatus ?? 'pending',
    };

    await this.promisifyRequest(store.put(updatedSyncMap));
  }

  async getSyncMap(entity: string): Promise<SyncMap | null> {
    await this.ensureInitialized();

    const transaction = this.db!.transaction(['syncMaps'], 'readonly');
    const store = transaction.objectStore('syncMaps');

    return await this.promisifyRequest(store.get(entity));
  }

  async getAllSyncMaps(): Promise<SyncMap[]> {
    await this.ensureInitialized();

    const transaction = this.db!.transaction(['syncMaps'], 'readonly');
    const store = transaction.objectStore('syncMaps');

    return await this.promisifyRequest(store.getAll());
  }

  async getSetting<T = unknown>(key: string): Promise<T | null> {
    await this.ensureInitialized();

    if (!this.db!.objectStoreNames.contains('settings')) {
      return null;
    }

    const transaction = this.createTransaction(['settings'], 'readonly');
    const store = transaction.objectStore('settings');
    const result = await this.promisifyRequest(store.get(key));

    return result ? (result.value as T) : null;
  }

  async setSetting<T = unknown>(key: string, value: T): Promise<void> {
    await this.ensureInitialized();

    if (!this.db!.objectStoreNames.contains('settings')) {
      return;
    }

    const transaction = this.createTransaction(['settings'], 'readwrite');
    const store = transaction.objectStore('settings');

    await this.promisifyRequest(
      store.put({
        key,
        value,
        updateTime: Date.now(),
      })
    );
  }

  async resetForSessionOwner(userId: string): Promise<void> {
    const nextOwner = userId;
    await this.ensureInitialized();

    const currentOwner = await this.getSetting<string>(this.sessionOwnerSettingKey);
    if (currentOwner === nextOwner) {
      return;
    }

    //include the objectstores to clear when different user is logged in.
    const storesToClear = ['tasks', 'inspection'];
    const existingStores = storesToClear.filter((storeName) =>
      this.db!.objectStoreNames.contains(storeName)
    );

    if (existingStores.length > 0) {
      const transaction = this.createTransaction(existingStores, 'readwrite');
      await Promise.all(
        existingStores.map((storeName) =>
          this.promisifyRequest(transaction.objectStore(storeName).clear())
        )
      );
    }

    if (this.db!.objectStoreNames.contains('syncMaps')) {
      const transaction = this.createTransaction(['syncMaps'], 'readwrite');
      await this.promisifyRequest(transaction.objectStore('syncMaps').delete('task'));
    }

    await this.setSetting(this.sessionOwnerSettingKey, nextOwner);
  }

  async getDatabaseSize(): Promise<number> {
    await this.ensureInitialized();
    if (!this.db) return 0;

    let size = 0;
    const stores = [
      'syncQueue',
      'presentations',
      'mainProducts',
      'price',
      'inspection',
      'establishments',
      'establishmentTypes',
      'locations',
      'productCategories',
      'baskets',
      'syncMaps',
    ];

    for (const storeName of stores) {
      if (!this.db!.objectStoreNames.contains(storeName)) {
        continue;
      }

      const transaction = this.db!.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const items = await this.promisifyRequest(store.getAll());
      size += JSON.stringify(items).length;
    }

    return size;
  }

  async resetDatabase(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.db) {
        this.db.close();
        this.db = null;
      }

      const deleteRequest = window.indexedDB.deleteDatabase(this.dbName);

      deleteRequest.onsuccess = () => {
        this.initPromise = null;
        resolve();
      };

      deleteRequest.onerror = () => {
        reject(deleteRequest.error);
      };

      deleteRequest.onblocked = () => {
        setTimeout(() => {
          resolve();
        }, 1000);
      };
    });
  }

  async forceResetDatabase(): Promise<void> {
    return new Promise((resolve) => {
      if (this.db) {
        this.db.close();
        this.db = null;
      }

      this.initPromise = null;

      const deleteRequest = window.indexedDB.deleteDatabase(this.dbName);

      deleteRequest.onsuccess = () => {
        resolve();
      };

      deleteRequest.onerror = () => {
        resolve();
      };

      deleteRequest.onblocked = () => {
        resolve();
      };
    });
  }

  private promisifyRequest<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
}

export const indexedDBManager = new IndexedDBManager();

export const resetIndexedDB = async (): Promise<void> => {
  await indexedDBManager.resetDatabase();
};

export const forceResetIndexedDB = async (): Promise<void> => {
  await indexedDBManager.forceResetDatabase();
};

if (typeof window !== 'undefined') {
  (window as any).resetIndexedDB = resetIndexedDB;
  (window as any).forceResetIndexedDB = forceResetIndexedDB;
  (window as any).indexedDBManager = indexedDBManager;
}
